import {reactive} from 'vue'
import {useUserStore, useSessionStore} from '@/stores'
import {useMeetingMachine} from '@/state'
import {useBroadcast} from '@/composables'
import {role} from '@/messages'
import {useEventBus} from '@vueuse/core'

// Shared, so the templates (MeetingView, AParticipants) and the handlers see the same state
const context = reactive<{counterparty?: any; role?: string}>({})

const resetContext = () => {
  Object.keys(context).forEach(key => delete (context as any)[key])
}

const finish = () => {
  const {send} = useMeetingMachine()
  setTimeout(() => {
    send('HIDE_ROLE')
    resetContext()
  }, 3000)
}

export const useRoleExchange = () => {
  const offerRole = (participant: any) => {
    const user = useUserStore()
    const broadcast = useBroadcast()
    const {send} = useMeetingMachine()
    send('OFFER_ROLE')
    context.counterparty = participant
    broadcast(role.offerMessage(user.id, participant.id))
    console.info(`offering ${user.party} role to participant ${participant.email}`)
  }

  const acceptOffer = () => {
    const user = useUserStore()
    const {send} = useMeetingMachine()
    const {counterparty} = context
    const broadcast = useBroadcast()
    broadcast(role.offerAcceptedMessage(user.id, counterparty.id))
    send('ACCEPT_OFFER')
    user.changeRole(counterparty.party)
    broadcast(role.updateMessage(user.id, user.party))
    finish()
    console.info(`accepted ${counterparty.party} role offered by ${counterparty.email}`)
  }

  const denyOffer = () => {
    const user = useUserStore()
    const {send} = useMeetingMachine()
    const {counterparty} = context
    const broadcast = useBroadcast()
    broadcast(role.offerDeniedMessage(user.id, counterparty.id))
    send('DENY_OFFER')
    finish()
    console.info(`denied ${counterparty.party} role offered by ${counterparty.email}`)
  }

  const cancel = () => {
    const user = useUserStore()
    const {send} = useMeetingMachine()
    const {counterparty} = context
    const broadcast = useBroadcast()
    broadcast(role.offerCanceledMessage(user.id, counterparty.id))
    send('CANCEL_OFFER')
    finish()
    console.info(`canceled ${user.party} role offer to ${counterparty.email}`)
  }

  return {context, offerRole, acceptOffer, denyOffer, cancel}
}

export const useRoleExchangeCallback = () => {
  const user = useUserStore()
  const session = useSessionStore()
  const {send} = useMeetingMachine()
  const broadcast = useBroadcast()

  useEventBus('role:offer').on((data: any) => {
    const {from: counterpartyId, to: partyId} = data
    const participant = session.getParticipant(counterpartyId)
    console.info(` ${participant.email} created ${participant.party} role offer`, {
      counterpartyId,
      partyId,
      userId: user.id,
    })
    if (Number(user.id) === Number(partyId)) {
      console.info(` ${participant.email} created ${participant.party} role offer`)
      context.counterparty = participant
      send('RECEIVED_ROLE_OFFER')
    }
  })

  useEventBus('role:offerAccepted').on((data: any) => {
    console.info('role offer accepted')
    const {to: id} = data
    if (Number(user.id) === Number(id)) {
      user.changeRole(context.counterparty.party)
      broadcast(role.updateMessage(user.id, user.party))
      finish()
      send('OFFER_ACCEPTED')
    }
  })

  useEventBus('role:offerDenied').on((data: any) => {
    console.info(`role offer denied`)
    const {to: id} = data
    if (Number(user.id) === Number(id)) {
      finish()
      send('OFFER_DENIED')
    }
  })

  useEventBus('role:offerCanceled').on((data: any) => {
    console.info('role offer canceled')
    const {to: id} = data
    if (Number(user.id) === Number(id)) {
      finish()
      send('OFFER_CANCELED')
    }
  })

  useEventBus('role:update').on((data: any) => {
    console.info(`received role update`, data)
    const {from: id, role: party} = data
    if (Number(user.id) !== Number(id)) {
      session.updateParticipant(id, {party})
    }
  })

  useEventBus('role:change').on((data: any) => {
    const {id, role: newRole} = data
    if (Number(user.id) === Number(id)) {
      console.info('received command to change role', {newRole})
      context.role = newRole
      user.changeRole(newRole)
      broadcast(role.updateMessage(id, newRole))
      finish()
      send('RECEIVED_ROLE_CHANGE')
    }
  })

  useEventBus('profile:create').on((data: any) => {
    const {id: participantId} = data
    const participant = session.getParticipant(participantId)
    const userInfo = session.getParticipantInfo(user.id)

    if (participant.party === 'assistant') {
      if (user.party === 'assistant') {
        broadcast(role.changeMessage(participantId, 'observer'))
        console.info(
          'Role duplication detected (current user and new participant has assistant role), sending role change to participant'
        )
      }
    }
    if (user.party === 'assistant' && userInfo.party !== 'assistant') {
      console.info('Detected that current user become assistant during the session, sending update')
      broadcast(role.updateMessage(user.id, user.party))
    } else if (user.party !== 'assistant' && userInfo.party === 'assistant') {
      console.info('Detected that current user granted assistant role during the session, sending update')
      broadcast(role.updateMessage(user.id, user.party))
    }
  })
}
